import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { WACHTWOORD_VERGETEN_PAD } from '@vip/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '../../design-system';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from './useAuth';

/**
 * Opgezet volgens shadcn's `login-03`: gedempte achtergrond, merk boven een
 * kaart met het formulier erin.
 *
 * Wat er uit dat block níet in zit: aanmelden met Apple of Google, een publieke
 * "maak een account"-link en algemene voorwaarden. VIP heeft geen van de drie,
 * want een account ontstaat uit een uitnodiging, en een knop die nergens heen
 * gaat is erger dan geen knop.
 *
 * De kaartkop staat daarom links en niet gecentreerd. `login-03` centreert hem,
 * maar dat block heeft een onderschrift en een rij aanmeldknoppen onder de kop:
 * een gecentreerde stapel van drie dingen. Hier is het één regel, en dan valt
 * centreren uit elkaar. `login-01`, het eenvoudige formulier, gebruikt een kale
 * `CardHeader` en dat is precies wat hier overblijft.
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /** ProtectedRoute geeft mee waar de sessie sneuvelde; daar keren we naar terug. */
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Er ging iets mis');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        {/* Het merk gecentreerd boven de kaart; de kop erin links. */}
        <span className="self-center">
          <Logo height={40} />
        </span>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Inloggen</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit}>
              <FieldGroup>
                <Field data-invalid={error ? true : undefined}>
                  <FieldLabel htmlFor="email">E-mailadres</FieldLabel>
                  <Input
                    aria-invalid={error ? true : undefined}
                    autoComplete="email"
                    id="email"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    value={email}
                  />
                </Field>

                <Field data-invalid={error ? true : undefined}>
                  <div className="flex items-center">
                    <FieldLabel htmlFor="password">Wachtwoord</FieldLabel>
                    <Link
                      className="ml-auto text-sm underline-offset-4 hover:underline"
                      to={WACHTWOORD_VERGETEN_PAD}
                    >
                      Wachtwoord vergeten?
                    </Link>
                  </div>
                  <Input
                    aria-invalid={error ? true : undefined}
                    autoComplete="current-password"
                    id="password"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    type="password"
                    value={password}
                  />
                  {/* Eén melding onder het formulier en niet per veld: de server
                      zegt niet wélk van de twee mis was, en dat hoort hij ook
                      niet te doen. */}
                  {error && <FieldError>{error}</FieldError>}
                </Field>

                <Field>
                  <Button disabled={busy} type="submit">
                    {busy && <Spinner data-icon="inline-start" />}
                    Inloggen
                  </Button>
                </Field>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
